import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { plata, fecha, hoy, primeroDelMes } from '../utils/formato';

// Los gastos del inmueble.
//
// Cargados al INMUEBLE y no al mes en que se pagaron, porque arreglar un techo
// sirve para el inquilino de ahora y para los tres siguientes. Es lo que hace
// que después se pueda decir cuál local de verdad deja plata.
//
// Las categorías van fijas a propósito: escritas a mano, en tres meses hay
// «arreglo», «Arreglos» y «reparación baño», y ya no se pueden sumar.

const TIPOS = [
  { id: 'reparacion',     ico: '🔧', nombre: 'Reparación' },
  { id: 'mejora',         ico: '🏗️', nombre: 'Mejora' },
  { id: 'predial',        ico: '🧾', nombre: 'Predial' },
  { id: 'administracion', ico: '🏛️', nombre: 'Administración' },
  { id: 'servicios',      ico: '💡', nombre: 'Servicios' },
  { id: 'otro',           ico: '📌', nombre: 'Otro' },
];

const VACIO = () => ({
  inmueble_id: '', fecha: hoy(), tipo: 'reparacion', monto: '',
  descripcion: '', forma_pago: 'efectivo',
});

function Gastos({ onCambio }) {
  const [gastos, setGastos] = useState([]);
  const [inmuebles, setInmuebles] = useState([]);
  const [desde, setDesde] = useState(primeroDelMes());
  const [hasta, setHasta] = useState(hoy());
  const [nuevo, setNuevo] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [aviso, setAviso] = useState(null);

  const cargar = useCallback(() => {
    axios.get('/api/gastos').then(r => setGastos(r.data || [])).catch(() => {});
    axios.get('/api/inmuebles').then(r => setInmuebles(r.data || [])).catch(() => {});
  }, []);

  useEffect(cargar, [cargar]);

  const nombreDe = (id) => (inmuebles.find(i => i.id === id) || {}).nombre || '—';
  const enRango = gastos.filter(g => g.fecha >= desde && g.fecha <= hasta);
  const total = enRango.reduce((s, g) => s + (g.monto || 0), 0);

  const porInmueble = inmuebles.map(i => ({
    ...i,
    total: enRango.filter(g => g.inmueble_id === i.id).reduce((s, g) => s + g.monto, 0),
  })).filter(i => i.total > 0).sort((a, b) => b.total - a.total);

  const guardar = async (e) => {
    e.preventDefault();
    const monto = parseFloat(nuevo.monto);
    if (!nuevo.inmueble_id) return setAviso({ tipo: 'malo', texto: 'Falta decir de qué inmueble' });
    if (!(monto > 0)) return setAviso({ tipo: 'malo', texto: 'El gasto tiene que ser mayor que cero' });
    setGuardando(true);
    try {
      await axios.post('/api/gastos', { ...nuevo, monto });
      setAviso({ tipo: 'bueno', texto: `${plata(monto)} anotados en ${nombreDe(parseInt(nuevo.inmueble_id))}` });
      setNuevo(null);
      cargar();
      onCambio && onCambio();
    } catch (err) {
      setAviso({ tipo: 'malo', texto: err.response?.data?.error || 'No se pudo guardar' });
    }
    setGuardando(false);
  };

  const borrar = async (g) => {
    if (!window.confirm(`¿Borrar el gasto de ${plata(g.monto)}?`)) return;
    try {
      await axios.delete(`/api/gastos/${g.id}`);
      cargar();
      onCambio && onCambio();
    } catch {
      setAviso({ tipo: 'malo', texto: 'No se pudo borrar' });
    }
  };

  return (
    <>
      <div className="encabezado">
        <h1>Gastos</h1>
        <p>Arreglos, predial y administración, cargados a cada inmueble</p>
      </div>

      {aviso && <div className={'aviso ' + aviso.tipo}>{aviso.texto}</div>}

      <div className="bloque">
        <div className="cuerpo">
          <div className="campos">
            <label className="campo">
              <span>Desde</span>
              <input type="date" value={desde} onChange={e => setDesde(e.target.value)} />
            </label>
            <label className="campo">
              <span>Hasta</span>
              <input type="date" value={hasta} onChange={e => setHasta(e.target.value)} />
            </label>
          </div>
        </div>
      </div>

      <div className="rejilla">
        <div className="dato" style={{ '--color': 'var(--malo)' }}>
          <div className="rotulo">🔧 Gastado</div>
          <div className="valor cifra">{plata(total)}</div>
          <div className="pie">
            {enRango.length === 1 ? '1 gasto' : `${enRango.length} gastos`} en el periodo
          </div>
        </div>
        {porInmueble.slice(0, 2).map(i => (
          <div key={i.id} className="dato" style={{ '--color': 'var(--acero)' }}>
            <div className="rotulo">🏢 {i.nombre}</div>
            <div className="valor cifra">{plata(i.total)}</div>
            <div className="pie">
              {total > 0 ? Math.round(i.total / total * 100) : 0}% de lo gastado
            </div>
          </div>
        ))}
      </div>

      <div className="bloque">
        <h2>🔧 Anotar un gasto</h2>
        <div className="cuerpo">
          {!nuevo ? (
            <div className="acciones">
              <button className="boton grande" disabled={inmuebles.length === 0}
                      onClick={() => setNuevo(VACIO())}>
                ➕ Nuevo gasto
              </button>
            </div>
          ) : (
            <form onSubmit={guardar}>
              <div className="campos">
                <label className="campo">
                  <span>De qué inmueble *</span>
                  <select value={nuevo.inmueble_id} required
                          onChange={e => setNuevo({ ...nuevo, inmueble_id: e.target.value })}>
                    <option value="">— Elegir —</option>
                    {inmuebles.map(i => <option key={i.id} value={i.id}>{i.nombre}</option>)}
                  </select>
                </label>
                <label className="campo">
                  <span>Cuánto *</span>
                  <input type="number" step="1" min="0" className="num-grande" required autoFocus
                         value={nuevo.monto} placeholder="0"
                         onChange={e => setNuevo({ ...nuevo, monto: e.target.value })} />
                </label>
                <label className="campo">
                  <span>Qué fue</span>
                  <select value={nuevo.tipo}
                          onChange={e => setNuevo({ ...nuevo, tipo: e.target.value })}>
                    {TIPOS.map(t => (
                      <option key={t.id} value={t.id}>{t.ico} {t.nombre}</option>
                    ))}
                  </select>
                </label>
                <label className="campo">
                  <span>Fecha</span>
                  <input type="date" value={nuevo.fecha}
                         onChange={e => setNuevo({ ...nuevo, fecha: e.target.value })} />
                </label>
                <label className="campo">
                  <span>Cómo pagó</span>
                  <select value={nuevo.forma_pago}
                          onChange={e => setNuevo({ ...nuevo, forma_pago: e.target.value })}>
                    <option value="efectivo">💵 Efectivo</option>
                    <option value="transferencia">🏦 Transferencia</option>
                  </select>
                </label>
              </div>
              <label className="campo" style={{ marginTop: 12 }}>
                <span>Detalle</span>
                <input value={nuevo.descripcion}
                       placeholder="Cambio de la teja del baño, pintura entre inquilinos…"
                       onChange={e => setNuevo({ ...nuevo, descripcion: e.target.value })} />
              </label>
              <div className="acciones">
                <button type="button" className="boton suave" onClick={() => setNuevo(null)}>
                  Cancelar
                </button>
                <button className="boton" disabled={guardando}>
                  {guardando ? 'Guardando…' : '💾 Guardar gasto'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>

      <div className="bloque">
        <h2>📋 Gastos del periodo <span className="contador">{enRango.length}</span></h2>
        {enRango.length === 0 ? (
          <div className="vacio">
            No hay gastos anotados en estas fechas. Mientras no se anoten, el
            rendimiento de cada inmueble se ve mejor de lo que es.
          </div>
        ) : (
          <div className="tabla-caja">
            <table className="datos">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Inmueble</th>
                  <th>Qué fue</th>
                  <th>Detalle</th>
                  <th style={{ textAlign: 'right' }}>Monto</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {enRango.map(g => {
                  const t = TIPOS.find(x => x.id === g.tipo);
                  return (
                    <tr key={g.id}>
                      <td>{fecha(g.fecha)}</td>
                      <td className="recio">{nombreDe(g.inmueble_id)}</td>
                      <td>{t ? `${t.ico} ${t.nombre}` : g.tipo}</td>
                      <td style={{ fontSize: 13 }}>{g.descripcion || '—'}</td>
                      <td className="num recio">{plata(g.monto)}</td>
                      <td>
                        <button className="boton chico suave" onClick={() => borrar(g)}>✕</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

export default Gastos;
